/**
 * BriefMap — the real Mapbox map for the brief's right pane, reusing the same
 * assembly as EditorMap: real basemap + WMS stencil overlays + ThreeLayer +
 * ZoneEditor, so manual drawing produces real geo-accurate Zones on
 * editor.world (a3) and the Overlay step shows a real WMS layer (a4). The
 * brief's left rail drives the active tool (CREATE to draw, SELECT otherwise).
 */
import { observer } from "mobx-react-lite";
import { ScaleControl } from "react-map-gl";
import { useEvent } from "src/hooks/useEvent";
import type { Editor } from "src/types/Editor";
import { ToolSelection } from "src/types/MapState";
import { WMS_CATALOG } from "src/constants/wmsCatalog";
import { Map as TessaMap } from "../Map/Map";
import { StencilMapLayers } from "../StencilMapLayers";
import { StencilOverlay } from "../StencilOverlay";
import { Compass3D } from "../CompassWrapper";
import { ThreeLayer } from "../Map/Layers/three-layer/Three.layer";
import { ZoneEditor } from "../Map/Layers/three-layer/ZoneEditor";
import type { WorldUpdateType } from "../Map/Layers/three-layer/WorldUpdateType";
import { Icon, ACCENT } from "./Icon";

const layersIcon = (
  <g>
    <path d="M4 5h16v14H4z" />
    <path d="M4 15l5-5 4 4 3-3 4 4" />
  </g>
);

export const BriefMap = observer(function BriefMap({
  editor,
  onZoneCreated,
}: {
  editor: Editor;
  onZoneCreated?: (zoneId: string) => void;
}) {
  const onSelectedToolCancel = useEvent(() => {
    editor.mapState.selectedTool.update(ToolSelection.SELECT);
  });
  const onWorldUpdate = useEvent((_type?: WorldUpdateType, createdZoneId?: string) => {
    if (createdZoneId) onZoneCreated?.(createdZoneId);
  });

  if (!editor.mapState.hasWorld || !editor.world) {
    return (
      <div className="absolute inset-0 grid place-items-center text-[12px] text-[#9ca3af]">
        Loading map…
      </div>
    );
  }

  const geoOrigin = { lng: editor.world.longitude, lat: editor.world.latitude };
  const activeOverlayId = editor.mapState.stencil.loadedCatalogId;
  const activeOverlay = activeOverlayId ? WMS_CATALOG.find((e) => e.id === activeOverlayId) : null;

  return (
    <div className="absolute inset-0">
      <TessaMap mapState={editor.mapState}>
        <StencilMapLayers mapState={editor.mapState} />
        <ThreeLayer origin={geoOrigin}>
          <ZoneEditor
            mapState={editor.mapState}
            defaultBrief={editor.defaultBrief}
            onWorldUpdate={onWorldUpdate}
            world={editor.world}
            onSelectedToolCancel={onSelectedToolCancel}
            selectedTool={editor.mapState.selectedTool.value.id}
            geoOrigin={geoOrigin}
            selection={editor.view.selection}
            enableWarnings={false}
          />
        </ThreeLayer>
        <ScaleControl />
      </TessaMap>

      <Compass3D mapState={editor.mapState} />

      {/* image-upload placement (floating image → drag/scale → lock) */}
      <StencilOverlay mapState={editor.mapState} />

      {/* active-overlay badge (the wireframe's on-map layer chip) */}
      {activeOverlay && (
        <div className="absolute top-2 left-3 z-20 pointer-events-none">
          <span className="inline-flex items-center gap-1.5 bg-white/95 border border-[#d1d5db] rounded-md px-2 py-0.5 shadow-sm text-[10px] text-[#4b5563]">
            <span style={{ color: ACCENT }}>
              <Icon d={layersIcon} size={11} />
            </span>
            {activeOverlay.title}
          </span>
        </div>
      )}
    </div>
  );
});
